import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { User, LogOut, Search, MessageCircle, X, Send, Heart, Download, Share, ChevronRight, Radio, Star, TrendingUp } from "lucide-react";
import { LikeButton } from "@/components/LikeButton";
import GoogleDriveLogo from "@/components/GoogleDriveLogo";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import axios from "axios";
import { UserAvatar } from "@/components/UserAvatar";
import { ProfilePhoto } from "@/components/ProfilePhoto";
import { FounderBadge } from "@/components/FounderBadge";
import { AmateurTvIcon, ChaturbateIcon, Cam4Icon, OlecamsIcon } from "@/components/CamPlatformIcons";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { useConsumerUnread } from "@/hooks/useConsumerUnread";
import { useAdminUnread } from "@/hooks/useAdminUnread";
// PWA INSTALL PROMPT — hook centralizado (fuente de verdad única)
import { usePwaInstallPrompt } from "@/hooks/usePwaInstallPrompt";
import { API } from "../lib/api";
import { useTranslation } from "react-i18next";
/**
 * InstallSection — pequeño bloque en el footer de HomePage.
 *
 * PWA INSTALL PROMPT:
 * Usa usePwaInstallPrompt() (fuente de verdad única del módulo) en vez de
 * registrar su propio beforeinstallprompt listener. Así evitamos tener dos
 * listeners que capturen el mismo evento y lo "consuman" por separado,
 * lo que provocaba el aviso de Chrome sobre user gesture.
 *
 * showInstallPrompt() se llama ÚNICAMENTE desde el onClick del botón.
 */
function InstallSection() {
  // ── PWA install state desde el hook centralizado ─────────────────────────
  const { canInstallPwa, showInstallPrompt, isInstalled } = usePwaInstallPrompt();
  const { t } = useTranslation();
  const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent) && !window.MSStream;

  if (isInstalled) {
    return (
      <p className="text-xs text-zinc-600 font-body text-center">
        STARTIP está instalado en tu dispositivo.
      </p>
    );
  }

  // ── MANEJADOR DE CLICK (gesto de usuario) ─────────────────────────────────
  // CRÍTICO: NO llamar showInstallPrompt() en useEffect ni código async
  // sin gesto de usuario. Solo aquí, en un handler de botón.
  const handleInstall = async () => {
    await showInstallPrompt();
  };

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-3 p-3 rounded-xl border border-border/60 bg-card/30"
      data-testid="footer-install-section">
      <div className="flex items-center gap-2.5">
        <img src="/icons/icon-72.png" alt="StarTip" className="w-8 h-8 rounded-lg shrink-0" />
        <div>
          <p className="text-xs font-heading font-bold text-white">{t("install.title")}</p>
          {isIOS ? (
            <p className="text-[11px] text-zinc-500 font-body">
              Toca <Share className="w-3 h-3 inline text-primary" /> y luego "Añadir a pantalla de inicio"
            </p>
          ) : (
            <p className="text-[11px] text-zinc-500 font-body">
              {canInstallPwa ? t("install.subtitle") : "Disponible en Chrome y Edge"}
            </p>
          )}
        </div>
      </div>
      {!isIOS && canInstallPwa && (
        <button
          onClick={handleInstall}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-primary/10 border border-primary/40 text-primary text-xs font-heading font-bold uppercase rounded-lg hover:bg-primary/20 active:scale-95 transition-all shrink-0"
          data-testid="footer-install-btn"
        >
          <Download className="w-3 h-3" />
          {t("install.install")}
        </button>
      )}
    </div>
  );
}

// Social icon renderer for the hero section
const SOCIAL_META = {
  instagram: { label: "Instagram en StarTip", bg: "linear-gradient(135deg,#fcaf45 0%,#f56040 35%,#e1306c 60%,#833ab4 100%)", glow: "rgba(220,39,67,0.5)" },
  tiktok:    { label: "TikTok de StarTip",    bg: "#010101", border: "1px solid #333", glow: "rgba(255,255,255,0.08)" },
  youtube:   { label: "YouTube de StarTip",   bg: "#FF0000", glow: "rgba(255,0,0,0.4)" },
  twitter:   { label: "X / Twitter de StarTip", bg: "#000000", border: "1px solid #444", glow: "rgba(255,255,255,0.12)" },
  facebook:  { label: "Facebook de StarTip",  bg: "#1877F2", glow: "rgba(24,119,242,0.4)" },
  onlyfans:  { label: "OnlyFans de StarTip",  bg: "#00AFF0", glow: "rgba(0,175,240,0.4)" },
  onlysafe:  { label: "OnlySafe de StarTip",  bg: "#6a4cff", glow: "rgba(106,76,255,0.4)" },
  telegram:  { label: "Telegram de StarTip",  bg: "#229ED9", glow: "rgba(34,158,217,0.4)" },
  gdrive:    { label: "Google Drive de StarTip", bg: "#FFFFFF", border: "1px solid #e5e5e5", glow: "rgba(255,255,255,0.25)" },
};

function HeroSocialIcon({ platform }) {
  const cls = "w-5 h-5";
  switch (platform) {
    case "instagram": return (
      <svg viewBox="0 0 24 24" className={cls} fill="none" stroke="white" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2.5" y="2.5" width="19" height="19" rx="5.5"/><circle cx="12" cy="12" r="4.8"/><circle cx="17.4" cy="6.6" r="1.15" fill="white" stroke="none"/>
      </svg>
    );
    case "tiktok": return (
      <svg viewBox="0 0 24 24" className={cls} fill="white">
        <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.31 6.31 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.33-6.34V8.69a8.18 8.18 0 004.78 1.52V6.73a4.85 4.85 0 01-1.01-.04z"/>
      </svg>
    );
    case "youtube": return (
      <svg viewBox="0 0 24 24" className={cls} fill="white">
        <path d="M23.5 6.2a3 3 0 00-2.1-2.1C19.5 3.6 12 3.6 12 3.6s-7.5 0-9.4.5A3 3 0 00.5 6.2C0 8.1 0 12 0 12s0 3.9.5 5.8a3 3 0 002.1 2.1c1.9.5 9.4.5 9.4.5s7.5 0 9.4-.5a3 3 0 002.1-2.1C24 15.9 24 12 24 12s0-3.9-.5-5.8zM9.7 15.5V8.5l6.3 3.5-6.3 3.5z"/>
      </svg>
    );
    case "twitter": return (
      <svg viewBox="0 0 24 24" className={cls} fill="white">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.73-8.835L2.25 2.25H8.08l4.253 5.622 5.911-5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
      </svg>
    );
    case "facebook": return (
      <svg viewBox="0 0 24 24" className={cls} fill="white">
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
      </svg>
    );
    case "onlyfans": return <svg viewBox="0 0 24 24" className={cls} fill="white"><text x="1" y="17" fontSize="13" fontWeight="bold" fontFamily="Arial,sans-serif">OF</text></svg>;
    case "onlysafe": return <svg viewBox="0 0 24 24" className={cls} fill="white"><text x="1" y="17" fontSize="13" fontWeight="bold" fontFamily="Arial,sans-serif">OS</text></svg>;
    case "telegram": return (
      <svg viewBox="0 0 24 24" className={cls} fill="white">
        <path d="M9.78 18.65l.28-4.23 7.68-6.92c.34-.31-.07-.46-.52-.19L7.74 13.3 3.64 12c-.88-.25-.89-.86.2-1.3l15.97-6.16c.73-.33 1.43.18 1.15 1.3l-2.72 12.81c-.19.91-.74 1.13-1.5.71L12.6 16.3l-1.99 1.93c-.23.23-.42.42-.83.42z"/>
      </svg>
    );
    case "gdrive": return (
      <GoogleDriveLogo className={cls} />
    );
    default: return <svg viewBox="0 0 24 24" className={cls} fill="none" stroke="white" strokeWidth="1.5"><circle cx="12" cy="12" r="9"/></svg>;
  }
}

// ── ¿Cómo Funcionamos? Modal ──────────────────────────────────────────────────
function HowItWorksModal({ onClose }) {
  const [customText, setCustomText] = useState(null); // null=cargando, ""=usar default
  const handleKey = (e) => { if (e.key === "Escape") onClose(); };

  useEffect(() => {
    axios.get(`${API}/promo-settings`)
      .then(r => setCustomText(r.data.how_it_works_text || ""))
      .catch(() => setCustomText(""));
  }, []);

  // Renderiza el texto plano del admin reproduciendo EXACTAMENTE el mismo diseño visual
  function renderCustomText(text) {
    const paras = text.split(/\n\n+/).map(p => p.trim()).filter(Boolean);
    const elements = [];
    let isIntro = true;          // primer párrafo = texto gris intro
    let importanteItems = [];    // acumula los párrafos del bloque IMPORTANTE
    let inImportante = false;    // dentro del recuadro dorado IMPORTANTE

    const flushImportante = (key) => {
      if (!importanteItems.length) return;
      elements.push(
        <section key={key}
          className="p-4 rounded-xl space-y-2"
          style={{ background: "rgba(212,175,55,0.08)", border: "1.5px solid rgba(212,175,55,0.38)" }}>
          {importanteItems}
        </section>
      );
      importanteItems = [];
      inImportante = false;
    };

    paras.forEach((para, idx) => {
      const lines = para.split("\n").map(l => l.trim()).filter(Boolean);
      if (!lines.length) return;
      const first = lines[0];

      // ── IMPORTANTE: bloque dorado especial ──────────────────────────────────
      const isImportanteHeader =
        first.toUpperCase().startsWith("IMPORTANTE") &&
        first === first.toUpperCase();

      if (isImportanteHeader) {
        flushImportante(`imp-prev-${idx}`);
        inImportante = true;
        importanteItems.push(
          <h4 key="imp-h" className="text-xs font-heading font-bold uppercase tracking-widest"
              style={{ color: "rgba(212,175,55,0.95)" }}>
            {first}
          </h4>
        );
        // Resto del párrafo → primera línea en zinc-200
        lines.slice(1).forEach((l, j) => {
          importanteItems.push(<p key={`imp-l-${j}`} className="text-zinc-200 text-xs">{l}</p>);
        });
        isIntro = false;
        return;
      }

      // Si seguimos dentro del bloque importante (párrafos siguientes)
      if (inImportante) {
        lines.forEach((l, j) => {
          importanteItems.push(<p key={`imp-c-${idx}-${j}`} className="text-zinc-400 text-[11px]">{l}</p>);
        });
        return;
      }

      // ── Sección en MAYÚSCULAS → cabecera dorada con separador ───────────────
      if (lines.length === 1 &&
          first === first.toUpperCase() &&
          first.length > 4 &&
          !/^[›•*\d]/.test(first)) {
        isIntro = false;
        elements.push(
          <React.Fragment key={`sec-${idx}`}>
            <hr style={{ borderColor: "rgba(212,175,55,0.18)" }} />
            <h4 className="text-xs font-heading font-bold uppercase tracking-widest"
                style={{ color: "rgba(212,175,55,0.90)" }}>
              {first}
            </h4>
          </React.Fragment>
        );
        return;
      }

      // ── Nota: → recuadro ámbar suave ────────────────────────────────────────
      if (/^nota:/i.test(first)) {
        const content = para.replace(/^nota:\s*/i, "").trim();
        isIntro = false;
        elements.push(
          <div key={`nota-${idx}`}
               className="p-3 rounded-lg text-[11px] text-zinc-400"
               style={{ background: "rgba(212,175,55,0.07)", border: "1px solid rgba(212,175,55,0.18)" }}>
            <strong className="text-zinc-300">Nota:</strong>{" "}{content}
          </div>
        );
        return;
      }

      // ── Lista de bullets (›, •) ──────────────────────────────────────────────
      if (lines.some(l => /^[›•]/.test(l))) {
        const bullets = [];
        const notes = [];
        lines.forEach((l, j) => {
          if (/^[›•]/.test(l)) {
            bullets.push(
              <li key={j} className="flex gap-2 text-zinc-300">
                <span className="shrink-0" style={{ color: "rgba(212,175,55,0.70)" }}>›</span>
                <span>{l.replace(/^[›•]\s*/, "")}</span>
              </li>
            );
          } else if (l.startsWith("*")) {
            notes.push(<p key={`n-${j}`} className="text-zinc-400 text-xs italic">{l.slice(1).trim()}</p>);
          } else {
            bullets.push(<li key={`r-${j}`} className="text-zinc-300 list-none">{l}</li>);
          }
        });
        isIntro = false;
        elements.push(<ul key={`ul-${idx}`} className="space-y-2 text-xs">{bullets}</ul>);
        if (notes.length) elements.push(...notes);
        return;
      }

      // ── Línea itálica (* prefijo) ────────────────────────────────────────────
      if (lines.length === 1 && first.startsWith("*")) {
        isIntro = false;
        elements.push(<p key={`it-${idx}`} className="text-zinc-400 text-xs italic">{first.slice(1).trim()}</p>);
        return;
      }

      // ── Párrafo con subtítulo en negrita (primera línea corta sin punto) ────
      if (lines.length >= 2 &&
          lines[0].length < 60 &&
          !lines[0].endsWith(".") &&
          !lines[0].endsWith(",")) {
        const bodyColor = isIntro ? "text-zinc-500" : "text-zinc-300";
        isIntro = false;
        elements.push(
          <div key={`sub-${idx}`} className="space-y-1">
            <p className="font-bold text-white text-sm">{lines[0]}</p>
            {lines.slice(1).map((l, j) => (
              <p key={j} className={`text-xs leading-relaxed ${bodyColor}`}>{l}</p>
            ))}
          </div>
        );
        return;
      }

      // ── Párrafo de texto normal ──────────────────────────────────────────────
      const textColor = isIntro ? "text-zinc-400" : "text-zinc-300";
      isIntro = false;
      elements.push(
        <p key={`p-${idx}`} className={`${textColor} text-xs leading-relaxed`}>
          {lines.join(" ")}
        </p>
      );
    });

    flushImportante("imp-final");
    return elements;
  }

  return (
    <>
      <div className="fixed inset-0 z-50 bg-black/65 backdrop-blur-sm" onClick={onClose} aria-hidden="true" />
      <div
        role="dialog" aria-modal="true" aria-label="¿Cómo funcionamos?"
        className="fixed inset-0 z-50 flex items-center justify-center px-4 py-6"
        style={{ pointerEvents: "none" }}
        onKeyDown={handleKey}
      >
        <div
          className="relative w-full max-w-2xl max-h-[85vh] overflow-y-auto rounded-xl"
          style={{ background: "rgba(20, 15, 2, 0.97)", border: "1.5px solid rgba(212,175,55,0.45)", pointerEvents: "auto" }}
        >
          <button onClick={onClose}
            className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-zinc-800/80 text-zinc-400 hover:text-white hover:bg-zinc-700 transition-all"
            data-testid="how-it-works-close-btn" aria-label="Cerrar">
            <X className="w-4 h-4" />
          </button>

          <div className="px-6 sm:px-8 py-7 space-y-4 text-sm font-body leading-relaxed">
            {/* Título siempre fijo */}
            <div className="pr-10">
              <h3 className="text-xl sm:text-2xl font-heading font-extrabold uppercase tracking-tight"
                style={{ color: "rgba(212,175,55,0.95)" }}>
                ¿Cómo funcionamos?
              </h3>
            </div>

            {/* Contenido: custom si está disponible, hardcoded mientras carga o si está vacío */}
            {customText === null ? (
              <p className="text-xs text-zinc-500 animate-pulse">Cargando…</p>
            ) : customText ? (
              <div className="space-y-3">{renderCustomText(customText)}</div>
            ) : (
              /* Fallback hardcoded original */
              <>
                <p className="mt-2 text-zinc-400 text-xs">
                  StarTip es la plataforma de cobros y pagos para vendedores de enlaces y servicios externos.
                  A continuación te explicamos cómo se reparten las comisiones y cómo puedes solicitar tus cobros.
                </p>
                <hr style={{ borderColor: "rgba(212,175,55,0.18)" }} />
                <section className="space-y-4">
                  <h4 className="text-xs font-heading font-bold uppercase tracking-widest" style={{ color: "rgba(212,175,55,0.90)" }}>Sistema de comisiones</h4>
                  <div className="space-y-1">
                    <p className="font-bold text-white text-sm">Vendedores estándar</p>
                    <p className="text-zinc-300 text-xs">Los vendedores estándar reciben el <span className="font-bold text-white">85%</span> de cada propina o pago en euros. La plataforma retiene el <span className="font-bold text-white">15%</span> restante como comisión por el servicio.</p>
                  </div>
                  <div className="space-y-1">
                    <p className="font-bold text-white text-sm">Vendedores Fundadores</p>
                    <p className="text-zinc-300 text-xs">Los vendedores con estatus de <span className="font-bold text-white">FUNDADOR</span> reciben el <span className="font-bold text-white">100%</span> de todas las propinas y pagos mientras conserven ese estatus, sin ninguna retención de comisión.</p>
                    <p className="text-zinc-500 text-[11px]">Una vez que dejen de ser Fundadores pasarán automáticamente a las condiciones estándar: 85% para vendedor y 15% para la plataforma.</p>
                  </div>
                  <div className="p-3 rounded-lg text-[11px] text-zinc-400" style={{ background: "rgba(212,175,55,0.07)", border: "1px solid rgba(212,175,55,0.18)" }}>
                    <strong className="text-zinc-300">Nota:</strong>{" "}La plataforma se reserva el derecho de modificar y mejorar los porcentajes de comisión y retención, por ejemplo para aplicar descuentos promocionales u otras campañas específicas.
                  </div>
                </section>
                <hr style={{ borderColor: "rgba(212,175,55,0.18)" }} />
                <section className="space-y-3">
                  <h4 className="text-xs font-heading font-bold uppercase tracking-widest" style={{ color: "rgba(212,175,55,0.90)" }}>Solicitar cobros</h4>
                  <p className="text-zinc-300 text-xs">El importe mínimo para solicitar un cobro es de <span className="font-bold text-white">50 €</span>.</p>
                  <ul className="space-y-2 text-xs">
                    <li className="flex gap-2 text-zinc-300"><span className="shrink-0" style={{ color: "rgba(212,175,55,0.70)" }}>›</span><span><strong className="text-white">De 50 € a 99,99 €:</strong> comisión fija de <strong className="text-white">5 €</strong>, descontada del importe enviado al vendedor.</span></li>
                    <li className="flex gap-2 text-zinc-300"><span className="shrink-0" style={{ color: "rgba(212,175,55,0.70)" }}>›</span><span><strong className="text-white">A partir de 100 €:</strong> comisión de <strong className="text-white">0 €</strong> si el cobro se realiza mediante transferencia IBAN.</span></li>
                    <li className="flex gap-2 text-zinc-300"><span className="shrink-0" style={{ color: "rgba(212,175,55,0.70)" }}>›</span><span><strong className="text-white">Cobro por PayPal:</strong> se aplica siempre una comisión adicional fija de <strong className="text-white">1 €</strong>, independientemente de la cantidad.</span></li>
                  </ul>
                  <p className="text-zinc-400 text-xs italic">* El mínimo de retiro puede verse modificado si la modelo ha recibido una promoción.</p>
                </section>
                <hr style={{ borderColor: "rgba(212,175,55,0.18)" }} />
                <section className="p-4 rounded-xl space-y-2" style={{ background: "rgba(212,175,55,0.08)", border: "1.5px solid rgba(212,175,55,0.38)" }}>
                  <h4 className="text-xs font-heading font-bold uppercase tracking-widest" style={{ color: "rgba(212,175,55,0.95)" }}>Importante: configura tu método de cobro</h4>
                  <p className="text-zinc-200 text-xs">Si no configuras ningún método de cobro, el <strong className="text-white">100% de las propinas y pagos</strong> que recibas se dirigirá directamente a la plataforma y <strong className="text-white">no se sumará a tu cartera</strong>.</p>
                  <p className="text-zinc-400 text-[11px]">Ese dinero <strong className="text-zinc-300">no es recuperable</strong> posteriormente, por lo que es tu responsabilidad configurar correctamente tu método de cobro antes de empezar a recibir ingresos.</p>
                </section>
              </>
            )}

            <div className="flex justify-center pt-1">
              <button onClick={onClose}
                className="px-6 py-2 rounded-lg font-heading font-bold text-xs uppercase tracking-wide transition-all hover:opacity-90 active:scale-95"
                style={{ background: "rgba(212,175,55,0.14)", border: "1px solid rgba(212,175,55,0.38)", color: "rgba(212,175,55,0.95)" }}
                data-testid="how-it-works-close-bottom-btn">
                Cerrar
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

// ── Promociones Activas Modal ─────────────────────────────────────────────────
function PromosModal({ onClose, user }) {
  const handleKey = (e) => { if (e.key === "Escape") onClose(); };

  const [promoTexts, setPromoTexts] = useState(null);
  const [fanName, setFanName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [existingRef, setExistingRef] = useState(null);
  const [refChecked, setRefChecked] = useState(false);

  const isCreator = user && user.role !== "consumer" && user.role !== "admin";

  useEffect(() => {
    axios.get(`${API}/promo-settings`).then(r => setPromoTexts(r.data)).catch(() => setPromoTexts({}));
    if (isCreator) {
      axios.get(`${API}/promotions/my-referral`, { withCredentials: true })
        .then(r => { setExistingRef(Object.keys(r.data).length ? r.data : null); })
        .catch(() => {})
        .finally(() => setRefChecked(true));
    } else {
      setRefChecked(true);
    }
  }, [isCreator]);

  const handleSubmitFan = async () => {
    if (!fanName.trim()) { toast.error("Por favor, escribe el nombre del Fan que te ha traído."); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/promotions/fan-referral`, { fan_name: fanName.trim() }, { withCredentials: true });
      toast.success(res.data.message, { duration: 6000 });
      setExistingRef(res.data.referral);
      setFanName("");
    } catch (e) { toast.error(e.response?.data?.detail || "Error al enviar"); }
    finally { setSubmitting(false); }
  };

  return (
    <>
      <div className="fixed inset-0 z-50 bg-black/65 backdrop-blur-sm" onClick={onClose} aria-hidden="true" />
      <div role="dialog" aria-modal="true" aria-label="Promociones Activas"
        className="fixed inset-0 z-50 flex items-center justify-center px-4 py-6"
        style={{ pointerEvents: "none" }} onKeyDown={handleKey}>
        <div className="relative w-full max-w-2xl max-h-[88vh] overflow-y-auto rounded-xl"
          style={{ background: "rgba(15,10,25,0.97)", border: "1.5px solid rgba(212,175,55,0.45)", pointerEvents: "auto" }}>
          {/* Close */}
          <button onClick={onClose}
            className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-zinc-800/80 text-zinc-400 hover:text-white hover:bg-zinc-700 transition-all"
            data-testid="promos-modal-close-btn" aria-label="Cerrar">
            <X className="w-4 h-4" />
          </button>

          <div className="px-6 sm:px-8 py-7 space-y-6 text-sm font-body leading-relaxed">
            {/* Title */}
            <div className="pr-10">
              <h3 className="text-xl sm:text-2xl font-heading font-extrabold uppercase tracking-tight"
                style={{ color: "rgba(212,175,55,0.95)" }}>
                Promociones Activas
              </h3>
              <p className="mt-1 text-zinc-400 text-xs">
                Beneficios especiales disponibles en StarTip ahora mismo.
              </p>
            </div>

            <hr style={{ borderColor: "rgba(212,175,55,0.18)" }} />

            {/* Block A — Creadoras */}
            {promoTexts?.creator_text && (
              <section className="space-y-2 p-4 rounded-xl"
                style={{ background: "rgba(212,175,55,0.07)", border: "1px solid rgba(212,175,55,0.2)" }}>
                <p className="text-[11px] font-heading font-bold uppercase tracking-widest"
                  style={{ color: "rgba(212,175,55,0.7)" }}>Solo para Creadoras</p>
                <h4 className="font-heading font-bold text-white text-base">Promociones para Creadoras</h4>
                <p className="text-zinc-300 text-sm whitespace-pre-wrap">{promoTexts.creator_text}</p>
              </section>
            )}

            {/* Block B — Fans VIP */}
            {promoTexts?.fan_text && (
              <section className="space-y-2 p-4 rounded-xl"
                style={{ background: "rgba(255,0,127,0.06)", border: "1px solid rgba(255,0,127,0.2)" }}>
                <p className="text-[11px] font-heading font-bold uppercase tracking-widest text-neon-pink/70">
                  Solo para Fans VIP
                </p>
                <h4 className="font-heading font-bold text-white text-base">Promociones para Fans VIP</h4>
                <p className="text-zinc-300 text-sm whitespace-pre-wrap">{promoTexts.fan_text}</p>
              </section>
            )}

            {promoTexts && !promoTexts.creator_text && !promoTexts.fan_text && (
              <p className="text-zinc-500 text-sm text-center py-4">No hay promociones activas en este momento.</p>
            )}

            {/* Block C — Fan referral form (creators only) */}
            {isCreator && refChecked && (
              <>
                <hr style={{ borderColor: "rgba(212,175,55,0.18)" }} />
                <section className="space-y-3">
                  <h4 className="font-heading font-bold text-white text-base">¿Quién te ha invitado?</h4>
                  <p className="text-xs text-zinc-400">
                    Si llegaste a esta plataforma gracias a un Fan VIP, escribe aquí su nombre de registro exactamente como aparece en la plataforma. Esto permitirá aplicar las promociones correspondientes.
                  </p>
                  {existingRef ? (
                    <div className="p-3 rounded-lg border border-emerald-500/30 bg-emerald-500/5 text-xs text-emerald-300">
                      Ya registraste a <strong>{existingRef.fan_name}</strong> como tu fan invitador.
                      Su beneficio está activo hasta el {existingRef.benefit_expires_at?.slice(0, 10)}.
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={fanName}
                        onChange={e => setFanName(e.target.value)}
                        placeholder="Nombre exacto del fan..."
                        className="flex-1 px-3 py-2 text-sm bg-zinc-900 border border-zinc-700 rounded-lg text-white placeholder:text-zinc-600 focus:outline-none focus:border-amber-400"
                        data-testid="modal-fan-referral-input"
                        onKeyDown={e => { if (e.key === "Enter") handleSubmitFan(); }}
                      />
                      <button onClick={handleSubmitFan} disabled={submitting || !fanName.trim()}
                        className="px-4 py-2 rounded-lg text-xs font-heading font-bold uppercase bg-amber-500/20 text-amber-300 border border-amber-500/40 hover:bg-amber-500/30 disabled:opacity-40 transition-all shrink-0"
                        data-testid="modal-fan-referral-submit">
                        {submitting ? "..." : "Enviar"}
                      </button>
                    </div>
                  )}
                </section>
              </>
            )}

            {/* Close button */}
            <button onClick={onClose}
              className="w-full py-2.5 rounded-xl border font-heading font-bold text-sm uppercase tracking-wider text-zinc-400 hover:text-white transition-colors"
              style={{ borderColor: "rgba(212,175,55,0.3)" }}>
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </>
  );
}


// ─── Avatar fallback — silhouette + gender gradient ───────────────────────────
function _avatarGradient(gender = "") {
  if (gender === "mujer")  return "linear-gradient(160deg, #be123c 0%, #9333ea 100%)";
  if (gender === "hombre") return "linear-gradient(160deg, #0369a1 0%, #0f766e 100%)";
  return "linear-gradient(160deg, #6d28d9 0%, #2563eb 100%)";
}

// Neutral person silhouette — no letter, works for any name.
function AvatarSilhouette() {
  return (
    <svg
      viewBox="0 0 100 120"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      style={{ width: "58%", height: "auto", opacity: 0.55 }}
    >
      {/* Head */}
      <circle cx="50" cy="36" r="22" fill="white" />
      {/* Shoulders / torso */}
      <path d="M5 120 Q5 72 50 72 Q95 72 95 120 Z" fill="white" />
    </svg>
  );
}


export default function HomePage() {
  // Toggle for the Filters UI on the search bar. Set to `true` to bring
  // it back — all the underlying state (showFilters, filters dropdown,
  // social filters, etc.) and backend params remain wired, only the
  // entry-point button is hidden.
  const SHOW_FILTERS_UI = false;

  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const [models, setModels] = useState([]);
  // ── No leídos por creador: {creator_user_id: count} — se carga si hay sesión
  const [creatorUnreads, setCreatorUnreads] = useState({});
  // ── Fundraising seen map: {user_id: {ts, count}} — feedback inmediato al hacer clic
  const [frSeen, setFrSeen] = useState({});

  // Used when the visitor is not logged in (logged-in users use has_unseen_updates from API).
  const checkUnseenLocal = (model) => {
    const updated = model.profile_updated_at;
    if (!updated) return false;
    try {
      const seen = localStorage.getItem(`startip_seen_${model.user_id}`);
      if (!seen) return true; // never visited before → glow
      return seen < updated;
    } catch (_) {
      return false;
    }
  };

  // Retorna { frNew, frNewCount } para el badge de recaudación.
  // frNew=true cuando hay algo nuevo/editado desde la última visita.
  // frNewCount = posts nuevos desde la última visita (0 si solo hubo ediciones).
  const checkFundraisingNew = (model) => {
    if (!model.has_active_fundraising || !model.fundraising_updated_at)
      return { frNew: false, frNewCount: 0 };
    try {
      // Primero usa el estado en memoria (actualizado al instante al hacer clic en tarjeta)
      let stored = frSeen[model.user_id];
      if (!stored) {
        const raw = localStorage.getItem(`startip_fr_seen_${model.user_id}`);
        if (!raw) {
          // Primera visita: todo es nuevo
          return { frNew: true, frNewCount: model.fundraising_post_count || 1 };
        }
        try { stored = JSON.parse(raw); }
        catch { stored = { ts: raw, count: 0 }; } // compatibilidad con formato antiguo (ISO string plano)
      }
      const frNew = stored.ts < model.fundraising_updated_at;
      // Solo muestra número si hay posts nuevos (no solo ediciones)
      const frNewCount = Math.max(0, (model.fundraising_post_count || 0) - (stored.count || 0));
      return { frNew, frNewCount };
    } catch (_) {
      return { frNew: false, frNewCount: 0 };
    }
  };

  // Marca la recaudación de una modelo como vista: guarda {ts, count} en localStorage
  // y actualiza el estado local para feedback visual inmediato.
  const markFundraisingSeen = (model) => {
    const val = { ts: new Date().toISOString(), count: model.fundraising_post_count || 0 };
    try { localStorage.setItem(`startip_fr_seen_${model.user_id}`, JSON.stringify(val)); } catch (_) {}
    setFrSeen(prev => ({ ...prev, [model.user_id]: val }));
  };

  // Retorna cuántas publicaciones premium nuevas tiene la creadora desde la última visita del fan.
  // Para fans LOGUEADOS: el backend ya envía el conteo personalizado por servicio suscrito → lo devolvemos directo.
  // Para ANÓNIMOS: comparamos el contador global del servidor contra el guardado en localStorage.
  const checkPremiumNewCount = (model) => {
    if (!model.has_premium_services) return 0;
    if (user) {
      // Logueado: backend ya filtró por suscripción activa → confiamos en ese valor
      return model.premium_new_posts_count || 0;
    }
    // Anónimo: comparación localStorage (incentivar login)
    const serverCount = model.premium_new_posts_count || 0;
    if (serverCount === 0) return 0;
    try {
      const raw = localStorage.getItem(`startip_premium_seen_${model.user_id}`);
      if (!raw) return serverCount; // nunca visitó → todo es nuevo
      let storedCount = 0;
      try { storedCount = JSON.parse(raw)?.count || 0; } catch { storedCount = 0; } // compat iso-string antiguo
      return Math.max(0, serverCount - storedCount);
    } catch {
      return 0;
    }
  };
  const [initialLoading, setInitialLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatTab, setChatTab] = useState("duda");
  const [chatText, setChatText] = useState("");
  // FAB: expandido por defecto; se persiste en localStorage si el usuario lo pliega
  const [fabExpanded, setFabExpanded] = useState(() => {
    try { return localStorage.getItem("startip_fab_collapsed") !== "true"; }
    catch { return true; }
  });
  const [sending, setSending] = useState(false);
  const [myMessages, setMyMessages] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const chatRef = useRef(null);
  const fabRef  = useRef(null);

  const [showHowItWorks, setShowHowItWorks] = useState(false);
  const [showPromos, setShowPromos] = useState(false);

  const { total: consumerUnread } = useConsumerUnread(user?.role === "consumer");
  const { count: adminUnread, refresh: refreshAdminUnread } = useAdminUnread(
    !!user && user.role !== "admin"
  );

  // Cerrar chat al hacer clic fuera
  useEffect(() => {
    const handler = (e) => {
      if (
        chatRef.current && !chatRef.current.contains(e.target) &&
        (!fabRef.current || !fabRef.current.contains(e.target))
      ) setChatOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleSendMessage = async () => {
    if (!chatText.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API}/messages`, { type: chatTab, content: chatText.trim() }, { withCredentials: true });
      toast.success("Mensaje enviado al admin");
      setChatText("");
      // Refresh history if already loaded
      if (myMessages.length > 0) fetchMyMessages();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al enviar");
    } finally {
      setSending(false);
    }
  };

  const fetchMyMessages = async () => {
    setLoadingHistory(true);
    try {
      const res = await axios.get(`${API}/messages/my`, { withCredentials: true });
      setMyMessages(res.data);
      refreshAdminUnread();
    } catch { /* silent */ }
    finally { setLoadingHistory(false); }
  };

  // Refresca el historial automáticamente cuando el usuario lo tiene abierto
  // Sin adminUnread en las deps para evitar el bucle infinito
  useEffect(() => {
    if (!chatOpen || chatTab !== "historial") return;
    fetchMyMessages(); // carga inmediata al abrir
    const id = setInterval(fetchMyMessages, 15000); // refresco cada 15 s mientras está abierto
    return () => clearInterval(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatOpen, chatTab]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [genderFilter, setGenderFilter] = useState("");
  const [activeTags, setActiveTags] = useState([]);
  const [activeSocials, setActiveSocials] = useState([]);
  const [liveFilter, setLiveFilter] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [platformSocials, setPlatformSocials] = useState([]);
  const filterRef = useRef(null);
  const ITEMS_PER_PAGE = 16;

  // Cerrar filtros al hacer clic fuera
  useEffect(() => {
    const handler = (e) => { if (filterRef.current && !filterRef.current.contains(e.target)) setShowFilters(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Fetch platform social links (public, no auth)
  useEffect(() => {
    axios.get(`${API}/platform-socials`).then(r => setPlatformSocials(r.data)).catch(() => {});
  }, []);

  const TAGS_OPTIONS = [
    { group: "Pelo", items: ["pelo largo", "media melena", "pelo corto", "sin pelo"] },
    { group: "Color de pelo", items: ["rubia/o", "morena/o", "castaño", "pelirrojo/a", "canas", "otro"] },
    { group: "Talla", items: ["talla baja", "talla media", "talla alta"] },
    { group: "Ojos", items: ["ojos marrones", "ojos verdes", "ojos azules"] },
    { group: "Piel", items: ["piel clara", "piel morena", "piel oscura"] },
  ];

  const SOCIAL_OPTIONS = [
    {
      key: "instagram", label: "Instagram",
      icon: (
        <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="white" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
          <rect x="2.5" y="2.5" width="19" height="19" rx="5.5" />
          <circle cx="12" cy="12" r="4.8" />
          <circle cx="17.4" cy="6.6" r="1.15" fill="white" stroke="none" />
        </svg>
      ),
      bg: "linear-gradient(135deg, #fcaf45 0%, #f77737 20%, #f56040 35%, #e1306c 60%, #c13584 80%, #833ab4 100%)",
    },
    {
      key: "tiktok", label: "TikTok",
      icon: (
        <svg viewBox="0 0 24 24" className="w-4 h-4" fill="white">
          <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.31 6.31 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V8.69a8.18 8.18 0 0 0 4.78 1.52V6.73a4.85 4.85 0 0 1-1.01-.04z"/>
        </svg>
      ),
      bg: "#010101",
    },
    {
      key: "youtube", label: "YouTube",
      icon: (
        <svg viewBox="0 0 24 24" className="w-4 h-4" fill="white">
          <path d="M23.5 6.2a3 3 0 0 0-2.1-2.1C19.5 3.6 12 3.6 12 3.6s-7.5 0-9.4.5A3 3 0 0 0 .5 6.2C0 8.1 0 12 0 12s0 3.9.5 5.8a3 3 0 0 0 2.1 2.1c1.9.5 9.4.5 9.4.5s7.5 0 9.4-.5a3 3 0 0 0 2.1-2.1C24 15.9 24 12 24 12s0-3.9-.5-5.8zM9.7 15.5V8.5l6.3 3.5-6.3 3.5z"/>
        </svg>
      ),
      bg: "#FF0000",
    },
    {
      key: "twitter", label: "X (Twitter)",
      icon: (
        <svg viewBox="0 0 24 24" className="w-4 h-4" fill="white">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.73-8.835L2.25 2.25H8.08l4.253 5.622 5.911-5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
        </svg>
      ),
      bg: "#000000",
    },
    {
      key: "facebook", label: "Facebook",
      icon: (
        <svg viewBox="0 0 24 24" className="w-4 h-4" fill="white">
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
        </svg>
      ),
      bg: "#1877F2",
    },
    {
      key: "onlyfans", label: "OnlyFans",
      icon: (
        <svg viewBox="0 0 24 24" className="w-4 h-4" fill="white">
          <text x="1" y="17" fontSize="13" fontWeight="bold" fontFamily="Arial,sans-serif">OF</text>
        </svg>
      ),
      bg: "#00AFF0",
    },
    {
      key: "onlysafe", label: "OnlySafe",
      icon: (
        <svg viewBox="0 0 24 24" className="w-4 h-4" fill="white">
          <text x="1" y="17" fontSize="13" fontWeight="bold" fontFamily="Arial,sans-serif">OS</text>
        </svg>
      ),
      bg: "#6a4cff",
    },
    {
      key: "fanvue", label: "Fanvue",
      icon: (
        <svg viewBox="0 0 24 24" className="w-4 h-4">
          <rect width="24" height="24" rx="4" fill="#B5FF1A"/>
          <text x="2" y="17" fontSize="11" fontWeight="900" fontFamily="Arial,sans-serif" fill="#111111">FV</text>
        </svg>
      ),
      bg: "#B5FF1A",
    },
    {
      key: "telegram", label: "Telegram",
      icon: (
        <svg viewBox="0 0 24 24" className="w-4 h-4" fill="white">
          <path d="M9.78 18.65l.28-4.23 7.68-6.92c.34-.31-.07-.46-.52-.19L7.74 13.3 3.64 12c-.88-.25-.89-.86.2-1.3l15.97-6.16c.73-.33 1.43.18 1.15 1.3l-2.72 12.81c-.19.91-.74 1.13-1.5.71L12.6 16.3l-1.99 1.93c-.23.23-.42.42-.83.42z"/>
        </svg>
      ),
      bg: "#229ED9",
    },
    {
      key: "google_drive", label: "Google Drive",
      icon: (
        <GoogleDriveLogo className="w-4 h-4" />
      ),
      bg: "#FFFFFF",
    },
  ];

  const CAM_SOCIAL_OPTIONS = [
    {
      key: "amateur_tv",
      label: "amateur.tv",
      icon: <AmateurTvIcon className="w-4 h-4" />,
      bg: "#222222",
    },
    {
      key: "chaturbate",
      label: "Chaturbate",
      icon: <ChaturbateIcon className="w-4 h-4" />,
      bg: "#FA7D1B",
    },
    {
      key: "cam4",
      label: "CAM4",
      icon: <Cam4Icon className="w-4 h-4" />,
      bg: "#1A1A1A",
    },
    {
      key: "olecams",
      label: "Olecams",
      icon: <OlecamsIcon className="w-4 h-4" />,
      bg: "#F57D22",
    },
  ];

  const buildQuery = (q, gender, tags, socials, live) => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (gender) params.set("gender", gender);
    if (tags.length) params.set("tags", tags.join(","));
    if (socials.length) params.set("social", socials.join(","));
    if (live) params.set("live", "true");
    const str = params.toString();
    return str ? `?${str}` : "";
  };

  // ── Admin: set gender directly from card ─────────────────────────────────
  const handleSetGender = async (e, modelUserId, newGender) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await axios.patch(`${API}/admin/models/${modelUserId}/gender`, { gender: newGender }, { withCredentials: true });
      setModels(prev => prev.map(m => m.user_id === modelUserId ? { ...m, gender: newGender } : m));
    } catch { /* silent */ }
  };

  const fetchModels = async (q = "", gender = "", tags = [], socials = [], isInitial = false, live = false) => {
    if (isInitial) setInitialLoading(true);
    else setSearching(true);
    try {
      const res = await axios.get(`${API}/models${buildQuery(q, gender, tags, socials, live)}`, { withCredentials: true });
      setModels(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      if (isInitial) setInitialLoading(false);
      else setSearching(false);
    }
  };

  useEffect(() => { fetchModels("", "", [], [], true, false); }, []);

  // ── Cargar no leídos por creador (solo si hay sesión) ────────────────────
  useEffect(() => {
    axios.get(`${API}/channels/creator-unreads`, { withCredentials: true })
      .then(r => setCreatorUnreads(r.data.unreads || {}))
      .catch(() => {}); // silencioso si no hay sesión
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchModels(search, genderFilter, activeTags, activeSocials, false, liveFilter);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search, genderFilter, activeTags, activeSocials, liveFilter]);

  const toggleTag = (tag) => {
    setActiveTags((prev) => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]);
  };

  const toggleSocial = (key) => {
    setActiveSocials((prev) => prev.includes(key) ? prev.filter(s => s !== key) : [...prev, key]);
  };

  const clearFilters = () => { setGenderFilter(""); setActiveTags([]); setActiveSocials([]); setLiveFilter(false); };

  const activeFilterCount = (genderFilter ? 1 : 0) + activeTags.length + activeSocials.length + (liveFilter ? 1 : 0);

  if (initialLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-neon-pink text-2xl font-heading font-bold tracking-wide">
          {t("loading")}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {showHowItWorks && <HowItWorksModal onClose={() => setShowHowItWorks(false)} />}
      {showPromos && <PromosModal onClose={() => setShowPromos(false)} user={user} />}
      <header className="border-b border-border px-4 py-5">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <img src="/logo.png" alt="StarTip logo" className="h-8 w-8 object-contain" />
            <h1 className="font-heading font-extrabold text-xl tracking-tight text-white uppercase">
              STAR<span className="text-neon-pink">TIP</span>
            </h1>
          </Link>
          <div className="flex items-center gap-3">
            {user ? (
              <>
                {/* Chat con admin — solo visible en desktop (sm+); en móvil se usa el FAB */}
                <div className="relative hidden sm:block">
                  <button
                    onClick={() => setChatOpen((o) => !o)}
                    className="relative flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white transition-colors font-heading font-bold uppercase"
                    data-testid="chat-admin-btn"
                  >
                    <MessageCircle className="w-4 h-4" />
                    <span className="hidden sm:inline">{t("header.chatAdmin")}</span>
                    {adminUnread > 0 && (
                      <span
                        className="absolute -top-2 -right-2 inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 rounded-full bg-orange-500 text-white text-[10px] font-heading font-bold animate-bounce"
                        data-testid="admin-unread-badge"
                      >
                        {adminUnread > 99 ? "99+" : adminUnread}
                      </span>
                    )}
                  </button>
                </div>

                <Link
                  to={user.role === "admin" ? "/admin" : user.role === "consumer" ? "/my-services" : "/dashboard"}
                  className="relative flex items-center gap-1.5 text-sm text-neon-pink hover:text-white transition-colors font-heading font-bold uppercase"
                  data-testid="user-panel-link"
                >
                  {user.role === "admin" ? t("header.admin") : user.role === "consumer" ? t("header.myServices") : t("header.myPanel")}
                  {user.role === "consumer" && consumerUnread > 0 && (
                    <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-primary text-white text-[10px] font-heading font-bold animate-bounce"
                      data-testid="consumer-homepage-unread-badge">
                      {consumerUnread > 99 ? "99+" : consumerUnread}
                    </span>
                  )}
                </Link>
                <button
                  onClick={logout}
                  className="p-1 text-zinc-400 hover:text-red-400"
                  data-testid="header-logout-btn"
                >
                  <LogOut className="w-4 h-4" />
                </button>
              </>
            ) : (
              <Link
                to="/login"
                className="flex items-center gap-1.5 text-sm text-neon-pink hover:text-white transition-colors font-heading font-bold uppercase"
                data-testid="login-link"
              >
                <User className="w-4 h-4" />
                {t("header.login")}
              </Link>
            )}
          </div>
        </div>
      </header>

      <section className="px-4 py-12 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Botón ¿Cómo funcionamos? */}
          <div className="flex flex-wrap justify-center gap-3 mb-5">
            <button
              onClick={() => setShowHowItWorks(true)}
              data-testid="how-it-works-btn"
              className="inline-flex items-center justify-center px-5 py-2.5 rounded-full text-sm font-heading font-bold uppercase tracking-wide transition-all duration-200 hover:scale-105 active:scale-95"
              style={{
                background: "rgba(212,175,55,0.13)",
                border: "1.5px solid #ff007f",
                color: "rgba(212,175,55,0.95)",
                backdropFilter: "blur(8px)",
                WebkitBackdropFilter: "blur(8px)",
                boxShadow: "0 0 14px rgba(255,0,127,0.18)",
              }}
            >
              {t("hero.howItWorks")}
            </button>
            <button
              onClick={() => setShowPromos(true)}
              data-testid="promos-btn"
              className="inline-flex items-center justify-center px-5 py-2.5 rounded-full text-sm font-heading font-bold uppercase tracking-wide transition-all duration-200 hover:scale-105 active:scale-95"
              style={{
                background: "rgba(212,175,55,0.13)",
                border: "1.5px solid rgba(212,175,55,0.6)",
                color: "rgba(212,175,55,0.95)",
                backdropFilter: "blur(8px)",
                WebkitBackdropFilter: "blur(8px)",
                boxShadow: "0 0 14px rgba(212,175,55,0.12)",
              }}
            >
              {t("hero.promotions")}
            </button>
          </div>

          <h2 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl text-white uppercase tracking-tight leading-tight">
            {t("hero.title")} <span className="text-neon-pink">{t("hero.titleHighlight")}</span>
          </h2>

          {/* Hero social links — dynamic, managed by admin */}
          {platformSocials.length > 0 && (
            <div className="flex items-center justify-center gap-5 mt-7" data-testid="hero-social-links">
              {platformSocials.map(({ key, url }) => {
                const meta = SOCIAL_META[key] || { label: key, bg: "#333", glow: "rgba(255,255,255,0.1)" };
                return (
                  <a
                    key={key}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={meta.label}
                    data-testid={`hero-social-${key}`}
                    className="inline-flex items-center justify-center w-12 h-12 rounded-2xl transition-all duration-200 hover:scale-110 active:scale-95"
                    style={{
                      background: meta.bg,
                      border: meta.border || "none",
                      boxShadow: `0 0 0 rgba(0,0,0,0)`,
                    }}
                    onMouseEnter={e => e.currentTarget.style.boxShadow = `0 0 18px ${meta.glow}`}
                    onMouseLeave={e => e.currentTarget.style.boxShadow = "none"}
                  >
                    <HeroSocialIcon platform={key} />
                  </a>
                );
              })}
            </div>
          )}

          <div className="mt-6 max-w-md mx-auto">
            <button
              onClick={() => setPrivacyOpen((o) => !o)}
              className="w-full text-left px-5 py-4 rounded-xl border-2 border-zinc-700 hover:border-neon-pink bg-zinc-900/60 transition-colors group"
              data-testid="privacy-accordion-toggle"
            >
              <div className="flex items-center justify-between gap-3">
                <span className="font-heading font-bold text-white text-sm sm:text-base leading-snug group-hover:text-neon-pink transition-colors">
                  {t("hero.privacyTitle")}
                </span>
                <span className={`text-zinc-400 transition-transform duration-300 shrink-0 ${privacyOpen ? "rotate-180" : ""}`}>
                  ▾
                </span>
              </div>
              {privacyOpen && (
                <p className="mt-3 text-sm text-zinc-400 font-body leading-relaxed whitespace-pre-line">
                  {t("hero.privacyText")}
                </p>
              )}
            </button>
          </div>
          <div className="mt-6 max-w-md mx-auto relative flex gap-2 items-center">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <Input
                type="text"
                placeholder={t("hero.searchPlaceholder")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-11 pr-10 bg-transparent border-2 border-border rounded-xl h-12 text-white placeholder:text-zinc-500 focus:border-neon-pink"
                data-testid="search-input"
              />
              {searching && (
                <div className="absolute right-4 top-1/2 -translate-y-1/2">
                  <div className="w-4 h-4 border-2 border-neon-pink border-t-transparent rounded-full animate-spin" />
                </div>
              )}
            </div>

            {/* Filter button — temporarily hidden by product decision.
                Logic kept intact in the codebase (state, dropdown, handlers)
                so flipping `SHOW_FILTERS_UI` back to true re-enables the
                whole UI without touching any other code. */}
            {SHOW_FILTERS_UI && (
            <div className="relative shrink-0" ref={filterRef}>
              <button
                onClick={() => setShowFilters(f => !f)}
                data-testid="toggle-filters-btn"
                className={`relative flex items-center gap-1.5 px-3 h-12 rounded-xl border-2 text-xs font-heading font-bold uppercase tracking-wide transition-all duration-200 ${
                  showFilters || activeFilterCount > 0
                    ? "border-neon-pink text-neon-pink bg-neon-pink/10"
                    : "border-border text-zinc-400 hover:border-zinc-500 hover:text-zinc-200"
                }`}
              >
                <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 shrink-0">
                  <path fillRule="evenodd" d="M3 3a1 1 0 011-1h12a1 1 0 011 1v3a1 1 0 01-.293.707L13 10.414V15a1 1 0 01-.553.894l-4 2A1 1 0 017 17v-6.586L3.293 6.707A1 1 0 013 6V3z" clipRule="evenodd"/>
                </svg>
                <span className="hidden sm:inline">{t("filters.label")}</span>
                {activeFilterCount > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 bg-neon-pink text-white text-[10px] w-4 h-4 flex items-center justify-center rounded-full font-body font-bold leading-none" data-testid="filter-count-badge">
                    {activeFilterCount}
                  </span>
                )}
              </button>

              {/* Dropdown */}
              {showFilters && (
                <div
                  className="absolute right-0 top-[calc(100%+8px)] w-72 bg-card border border-border rounded-2xl shadow-2xl z-50 overflow-hidden"
                  data-testid="filter-panel"
                >
                  <div className="px-4 py-3 border-b border-border flex items-center justify-between">
                    <p className="text-xs font-heading font-bold text-white uppercase tracking-wide">{t("filters.label")}</p>
                    {activeFilterCount > 0 && (
                      <button onClick={clearFilters} data-testid="clear-filters-btn"
                        className="text-[11px] text-zinc-500 hover:text-neon-pink font-body underline transition-colors">
                        {t("filters.clearAll")}
                      </button>
                    )}
                  </div>

                  <div className="px-4 py-3 space-y-4 max-h-[70vh] overflow-y-auto">
                    {/* Gender */}
                    <div>
                      <p className="text-[10px] text-zinc-400 font-body uppercase tracking-wider mb-2">{t("filters.gender")}</p>
                      <div className="flex flex-wrap gap-1.5">
                        {[{ val: "", label: t("filters.all") }, { val: "mujer", label: t("filters.women") }, { val: "hombre", label: t("filters.men") }].map(({ val, label }) => (
                          <button key={val} onClick={() => setGenderFilter(val)}
                            data-testid={`filter-gender-${val || "all"}`}
                            className={`px-2.5 py-1 rounded-full text-[11px] font-body border-2 transition-all ${
                              genderFilter === val
                                ? "bg-neon-pink border-neon-pink text-white"
                                : "border-border text-zinc-400 hover:border-neon-pink/50"
                            }`}>
                            {label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Redes Sociales */}
                    <div>
                      <p className="text-[10px] text-zinc-400 font-body uppercase tracking-wider mb-2">{t("filters.socialNetworks")}</p>
                      <div className="flex flex-wrap gap-2">
                        {SOCIAL_OPTIONS.map(({ key, label, icon, bg }) => {
                          const active = activeSocials.includes(key);
                          return (
                            <button
                              key={key}
                              onClick={() => toggleSocial(key)}
                              data-testid={`filter-social-${key}`}
                              title={label}
                              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-body border-2 transition-all duration-150 ${
                                active
                                  ? "border-transparent text-white shadow-sm"
                                  : "border-border text-zinc-400 hover:border-zinc-500 bg-transparent"
                              }`}
                              style={active ? { background: bg, borderColor: "transparent" } : {}}
                            >
                              <span className={`flex items-center justify-center w-4 h-4 rounded-full shrink-0 ${active ? "" : "opacity-70"}`}
                                style={{ background: bg }}>
                                {icon}
                              </span>
                              {label}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Plataformas Webcam */}
                    <div>
                      <p className="text-[10px] text-orange-400/80 font-body uppercase tracking-wider mb-2">{t("filters.webcamLive")}</p>
                      <div className="flex flex-wrap gap-2">
                        {CAM_SOCIAL_OPTIONS.map(({ key, label, icon, bg }) => {
                          const active = activeSocials.includes(key);
                          return (
                            <button
                              key={key}
                              onClick={() => toggleSocial(key)}
                              data-testid={`filter-social-${key}`}
                              title={label}
                              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-body border-2 transition-all duration-150 ${
                                active
                                  ? "border-transparent text-white shadow-sm"
                                  : "border-orange-900/40 text-zinc-400 hover:border-orange-500/40 bg-transparent"
                              }`}
                              style={active ? { background: bg, borderColor: "transparent" } : {}}
                            >
                              <span className={`flex items-center justify-center w-4 h-4 rounded-full shrink-0 ${active ? "" : "opacity-70"}`}
                                style={{ background: bg }}>
                                {icon}
                              </span>
                              {label}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Tags */}
                    {TAGS_OPTIONS.map(({ group, items }) => (
                      <div key={group}>
                        <p className="text-[10px] text-zinc-500 font-body uppercase tracking-wider mb-1.5">{group}</p>
                        <div className="flex flex-wrap gap-1.5">
                          {items.map((tag) => {
                            const active = activeTags.includes(tag);
                            return (
                              <button key={tag} onClick={() => toggleTag(tag)}
                                data-testid={`filter-tag-${tag.replace(/[/ ]/g, "-")}`}
                                className={`px-2.5 py-1 rounded-full text-[11px] font-body border transition-all ${
                                  active
                                    ? "bg-neon-cyan/20 border-neon-cyan text-neon-cyan"
                                    : "border-border text-zinc-500 hover:border-neon-cyan/40"
                                }`}>
                                {tag}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            )}
          </div>
        </motion.div>
      </section>

      <section className="px-4 pb-16">
        {/* ── EN DIRECTO AHORA toggle ─────────────────────────────────── */}
        <div className="max-w-4xl mx-auto mb-5 flex justify-center">
          <button
            onClick={() => setLiveFilter(v => !v)}
            data-testid="live-filter-btn"
            className={`group relative flex items-center gap-2.5 px-5 py-2.5 rounded-full border-2 text-sm font-heading font-bold uppercase tracking-widest transition-all duration-300 ${
              liveFilter
                ? "border-red-500 bg-red-600/15 text-red-400 shadow-lg shadow-red-600/25"
                : "border-red-600/35 text-red-400/60 hover:border-red-500/70 hover:text-red-400 hover:bg-red-600/8"
            }`}
          >
            {/* Pulsing dot */}
            <span className="relative flex h-2.5 w-2.5 shrink-0">
              {liveFilter && (
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-70" />
              )}
              <span className={`relative inline-flex rounded-full h-2.5 w-2.5 ${liveFilter ? "bg-red-500" : "bg-red-600/50"}`} />
            </span>
            {t("live.liveNow")}
            {liveFilter && (
              <span className="ml-0.5 bg-red-600 text-white text-[10px] px-1.5 py-0.5 rounded-full font-body font-bold leading-tight">
                {models.length}
              </span>
            )}
          </button>
        </div>

        {/* ── Empty state when live filter active ────────────────────── */}
        {liveFilter && models.length === 0 && !searching && (
          <div className="max-w-4xl mx-auto text-center py-16">
            <p className="text-zinc-500 font-body text-base">{t("live.noLive")}</p>
            <button onClick={() => setLiveFilter(false)} className="mt-3 text-xs text-red-400/70 underline font-body">
              {t("live.showAll")}
            </button>
          </div>
        )}

        {(() => {
          const totalPages = Math.ceil(models.length / ITEMS_PER_PAGE);
          const pagedModels = models.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);
          return (
            <>
              <div className="max-w-4xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-4">
                {pagedModels.map((model, i) => (
                  <motion.div
                    key={model.user_id}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: i * 0.05 }}
                  >
                    <Link
                      to={`/tip/${model.slug || model.user_id}`}
                      className={`group relative block overflow-hidden rounded-xl border-2 border-border bg-card hover:border-neon-pink transition-all duration-300${
                        (user ? model.has_unseen_updates : checkUnseenLocal(model)) || checkPremiumNewCount(model) > 0
                          ? " profile-update-glow"
                          : ""
                      }`}
                      data-testid={`model-card-${model.user_id}`}
                      title={
                        checkPremiumNewCount(model) > 0
                          ? `${checkPremiumNewCount(model)} publicaciones premium nuevas`
                          : (user ? model.has_unseen_updates : checkUnseenLocal(model))
                            ? "Este perfil ha sido actualizado desde tu última visita"
                            : undefined
                      }
                      onClick={() => {
                        if (model.has_active_fundraising) markFundraisingSeen(model);
                      }}
                    >
                      <ProfilePhoto
                        src={model.photo_url || null}
                        alt={model.name}
                        privacyMode={model.avatar_privacy_mode || "normal"}
                        imgClassName="group-hover:scale-105 transition-transform duration-500"
                        fallback={
                          <div className="absolute inset-0 w-full h-full flex items-end justify-center" style={{ background: _avatarGradient(model.gender) }}>
                            <AvatarSilhouette />
                          </div>
                        }
                      />
                      {/* Founder badge — top-left overlay */}
                      {model.is_founder && (
                        <div className="absolute top-2 left-2 z-10">
                          <FounderBadge size="md" />
                        </div>
                      )}
                      {/* EN DIRECTO badge — webcam live indicator (top-center) */}
                      {model.any_live_active && (
                        <div className="absolute top-2 left-1/2 -translate-x-1/2 z-20 flex items-center gap-1 px-2 py-0.5 rounded-full" style={{ background: "rgba(220,38,38,0.92)", backdropFilter: "blur(4px)" }} data-testid={`live-badge-${model.user_id}`}>
                          <span className="relative flex h-1.5 w-1.5">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
                            <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-white" />
                          </span>
                          <span className="text-white text-[9px] font-heading font-bold uppercase tracking-widest whitespace-nowrap">{t("live.badge")}</span>
                        </div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                      <div className="absolute bottom-0 left-0 right-0 p-4">
                        {/* Badge "ACTUALIZADO" — solo para actualizaciones generales del perfil/servicios */}
                        {(user ? model.has_unseen_updates : checkUnseenLocal(model)) && (
                          <span
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-heading font-bold uppercase tracking-wide mb-1.5"
                            style={{
                              background: "rgba(255,0,127,0.18)",
                              color: "rgba(255,190,220,0.90)",
                              border: "1px solid rgba(255,0,127,0.32)",
                            }}
                            data-testid={`updated-badge-${model.user_id}`}
                          >
                            {t("cards.updated")}
                          </span>
                        )}
                        <h3 className="font-heading font-bold text-lg text-white">
                          {model.name}
                        </h3>
                        {model.platform_staff && (
                          <span
                            className="inline-flex items-center gap-0.5 mt-0.5 px-1.5 py-0.5 rounded-full text-[8px] font-heading font-black uppercase tracking-wide bg-amber-500/25 border border-amber-500/50 text-amber-400"
                            data-testid={`staff-badge-card-${model.user_id}`}
                          >
                            <Star className="w-2 h-2 fill-current" />StarTip Staff
                          </span>
                        )}
                        <p className="text-sm text-zinc-300 line-clamp-1 font-body">
                          {model.description}
                        </p>
                        {/* Admin-only gender picker — sets avatar gradient color (only shown when no photo) */}
                        {user?.role === "admin" && !model.photo_url && (
                          <div className="flex gap-1.5 mt-2" onClick={e => { e.preventDefault(); e.stopPropagation(); }}>
                            <button
                              onClick={e => handleSetGender(e, model.user_id, "mujer")}
                              data-testid={`admin-gender-mujer-${model.user_id}`}
                              className="flex items-center gap-0.5 px-2 py-0.5 rounded-full text-[9px] font-heading font-bold uppercase tracking-wide transition-all"
                              style={model.gender === "mujer"
                                ? { background: "rgba(190,18,60,0.6)", color: "#fda4af", border: "1px solid rgba(244,63,94,0.6)" }
                                : { background: "rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.45)", border: "1px solid rgba(255,255,255,0.15)" }}
                            >
                              ♀ Mujer
                            </button>
                            <button
                              onClick={e => handleSetGender(e, model.user_id, "hombre")}
                              data-testid={`admin-gender-hombre-${model.user_id}`}
                              className="flex items-center gap-0.5 px-2 py-0.5 rounded-full text-[9px] font-heading font-bold uppercase tracking-wide transition-all"
                              style={model.gender === "hombre"
                                ? { background: "rgba(3,105,161,0.6)", color: "#7dd3fc", border: "1px solid rgba(14,165,233,0.6)" }
                                : { background: "rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.45)", border: "1px solid rgba(255,255,255,0.15)" }}
                            >
                              ♂ Hombre
                            </button>
                          </div>
                        )}
                      </div>
                      {/* Like button — stops propagation so no navigate on click */}
                      <div className="absolute top-2 right-2 z-10" onClick={e => e.preventDefault()}>
                        <LikeButton
                          creatorId={model.user_id}
                          initialCount={model.likes_count || 0}
                          size="sm"
                          showCount={true}
                        />
                      </div>
                      {/* Badges verticales — margen derecho, centrados en la tarjeta */}
                      {(model.has_channels || model.has_premium_services || model.has_active_fundraising) && (
                        <div
                          className="absolute right-2 z-10 flex flex-col gap-2 items-center"
                          style={{ top: "50%", transform: "translateY(-50%)" }}
                          onClick={e => e.preventDefault()}
                        >
                          {model.has_channels && (
                            <div
                              className="relative"
                              data-testid={`channel-card-badge-${model.user_id}`}
                              title="Esta creadora tiene un canal de difusión"
                            >
                              <div
                                className="flex items-center justify-center w-7 h-7 rounded-full border-2 border-black shadow-lg"
                                style={{ background: "#2AABEE" }}
                              >
                                <Radio className="w-3.5 h-3.5 text-white" />
                              </div>
                              {(creatorUnreads[model.user_id] > 0) && (
                                <span
                                  className="absolute -top-1.5 -left-1.5 bg-[#39d98a] text-black text-[8px] font-black rounded-full min-w-[14px] h-3.5 flex items-center justify-center px-0.5 leading-none shadow"
                                  data-testid={`channel-unread-${model.user_id}`}
                                >
                                  {creatorUnreads[model.user_id] >= 99 ? "99+" : creatorUnreads[model.user_id]}
                                </span>
                              )}
                            </div>
                          )}
                          {model.has_premium_services && (() => {
                            const newCount = checkPremiumNewCount(model);
                            return (
                              <div
                                className="relative"
                                data-testid={`premium-card-badge-${model.user_id}`}
                                title={newCount > 0 ? `${newCount} publicaciones premium nuevas` : "Esta creadora tiene contenido premium"}
                              >
                                <div
                                  className={`flex items-center justify-center w-7 h-7 rounded-full border-2 border-black shadow-lg${newCount > 0 ? " animate-pulse" : ""}`}
                                  style={{
                                    background: newCount > 0 ? "#7c3aed" : "#9333ea",
                                    boxShadow: newCount > 0 ? "0 0 12px rgba(139,92,246,0.75)" : undefined,
                                  }}
                                >
                                  <Star className="w-3.5 h-3.5 text-white fill-white" />
                                </div>
                                {newCount > 0 && (
                                  <span
                                    className="absolute -top-1.5 -left-1.5 bg-violet-500 text-white text-[8px] font-black rounded-full min-w-[14px] h-3.5 flex items-center justify-center px-0.5 leading-none shadow"
                                    data-testid={`premium-new-count-${model.user_id}`}
                                  >
                                    {newCount >= 99 ? "99+" : newCount}
                                  </span>
                                )}
                              </div>
                            );
                          })()}
                          {model.has_active_fundraising && (() => {
                            const { frNew, frNewCount } = checkFundraisingNew(model);
                            return (
                              <div className="relative">
                                <div
                                  className={`flex items-center justify-center w-7 h-7 rounded-full border-2 border-black shadow-lg${frNew ? " animate-pulse" : ""}`}
                                  style={{
                                    background: "#16a34a",
                                    boxShadow: frNew ? "0 0 12px rgba(22,163,74,0.80)" : undefined,
                                  }}
                                  title={frNew ? "Recaudación actualizada desde tu última visita" : "Esta creadora tiene una recaudación activa"}
                                  data-testid={`fundraising-card-badge-${model.user_id}`}
                                >
                                  <TrendingUp className="w-3.5 h-3.5 text-white" />
                                </div>
                                {frNew && frNewCount > 0 && (
                                  <span className="absolute -top-1 -right-1 min-w-[14px] h-[14px] rounded-full bg-red-500 border border-black text-white text-[8px] font-black flex items-center justify-center leading-none px-0.5"
                                    data-testid={`fundraising-new-count-${model.user_id}`}>
                                    {frNewCount >= 99 ? "99+" : frNewCount}
                                  </span>
                                )}
                              </div>
                            );
                          })()}
                        </div>
                      )}
                    </Link>
                  </motion.div>
                ))}
              </div>

              {models.length === 0 && !searching && (
                <div className="text-center py-16 text-zinc-500">
                  <p className="font-body text-lg">
                    {search ? t("cards.noResults", { search }) : t("cards.noModels")}
                  </p>
                </div>
              )}

              {models.length > ITEMS_PER_PAGE && (
                <div className="max-w-4xl mx-auto flex items-center justify-center gap-4 mt-10">
                  <button
                    onClick={() => { setPage((p) => p - 1); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                    disabled={page === 1}
                    data-testid="pagination-prev"
                    className={`px-6 py-3 rounded-xl border-2 font-heading font-bold text-sm uppercase tracking-widest transition-all duration-200 ${
                      page === 1
                        ? "border-zinc-800 text-zinc-700 cursor-not-allowed"
                        : "border-border text-zinc-300 hover:border-neon-pink hover:text-neon-pink active:scale-95"
                    }`}
                  >
                    {t("pagination.previous")}
                  </button>

                  <span className="font-heading text-sm text-zinc-500 tabular-nums" data-testid="pagination-info">
                    {page} / {totalPages}
                  </span>

                  <button
                    onClick={() => { setPage((p) => p + 1); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                    disabled={page === totalPages}
                    data-testid="pagination-next"
                    className={`px-6 py-3 rounded-xl border-2 font-heading font-bold text-sm uppercase tracking-widest transition-all duration-200 ${
                      page === totalPages
                        ? "border-zinc-800 text-zinc-700 cursor-not-allowed"
                        : "border-border text-zinc-300 hover:border-neon-pink hover:text-neon-pink active:scale-95"
                    }`}
                  >
                    {t("pagination.next")}
                  </button>
                </div>
              )}
            </>
          );
        })()}
      </section>

      {/* Footer legal */}
      <footer className="border-t border-border px-4 py-6 mt-4">
        <div className="max-w-4xl mx-auto space-y-4">
          {/* Install section */}
          <InstallSection />

          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-2 border-t border-border/40">
            <p className="text-xs text-zinc-600 font-body">
              © {new Date().getFullYear()} Startip S.L.
            </p>
            <div className="flex flex-wrap gap-4 text-xs text-zinc-500 font-body">
              <Link to="/aviso-legal" className="hover:text-neon-pink transition-colors" data-testid="footer-aviso-legal">{t("footer.legal")}</Link>
              <Link to="/privacidad" className="hover:text-neon-pink transition-colors" data-testid="footer-privacidad">{t("footer.privacy")}</Link>
              <Link to="/cookies" className="hover:text-neon-pink transition-colors" data-testid="footer-cookies">{t("footer.cookies")}</Link>
              <a href="mailto:startipweb@gmail.com" className="hover:text-neon-pink transition-colors">{t("footer.contact")}</a>
            </div>
          </div>
        </div>
      </footer>

      {/* ── Chat dropdown — nivel raíz para funcionar tanto en desktop (header) como en móvil (FAB) ── */}
      {user && chatOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/60 z-40"
            onClick={() => setChatOpen(false)}
          />
          <div
            ref={chatRef}
            className="fixed left-4 right-4 top-1/2 -translate-y-1/2 sm:left-auto sm:translate-y-0 sm:top-16 sm:right-4 sm:w-80 bg-card border-2 border-border rounded-2xl shadow-2xl z-50 overflow-hidden flex flex-col max-h-[82vh] sm:max-h-[520px]"
            data-testid="chat-dropdown"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-border">
              <p className="font-heading font-bold text-white text-sm uppercase">{t("chat.title")}</p>
              <button onClick={() => setChatOpen(false)} className="text-zinc-500 hover:text-white">
                <X className="w-4 h-4" />
              </button>
            </div>
            {/* Tabs */}
            <div className="flex border-b border-border">
              {[
                { key: "duda", label: "Dudas" },
                { key: "mejora", label: "Mejoras" },
                { key: "historial", label: "Historial" },
              ].map(({ key, label }) => (
                <button key={key}
                  onClick={() => { setChatTab(key); if (key === "historial") fetchMyMessages(); }}
                  className={`relative flex-1 py-2 text-xs font-heading font-bold uppercase transition-colors ${chatTab === key ? "text-neon-pink border-b-2 border-neon-pink bg-neon-pink/5" : "text-zinc-500 hover:text-white"}`}
                  data-testid={`chat-tab-${key}`}>
                  {label}
                  {key === "historial" && adminUnread > 0 && (
                    <span
                      className="absolute top-1 right-1 inline-flex items-center justify-center min-w-[14px] h-[14px] px-0.5 rounded-full bg-orange-500 text-white text-[9px] font-heading font-bold"
                      data-testid="historial-unread-dot"
                    >
                      {adminUnread > 9 ? "9+" : adminUnread}
                    </span>
                  )}
                </button>
              ))}
            </div>
            {/* Body */}
            <div className="overflow-y-auto flex-1">
              {chatTab !== "historial" ? (
                <div className="p-4 space-y-3">
                  <p className="text-xs text-zinc-500 font-body">
                    {chatTab === "duda" ? "¿Tienes alguna pregunta sobre la plataforma?" : "¿Tienes alguna sugerencia de mejora?"}
                  </p>
                  <textarea
                    value={chatText}
                    onChange={(e) => setChatText(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSendMessage(); }}}
                    placeholder={t("chat.placeholder")}
                    rows={3}
                    maxLength={1000}
                    className="w-full bg-transparent border-2 border-border rounded-xl px-3 py-2 text-sm text-white placeholder:text-zinc-600 resize-none focus:outline-none focus:border-neon-pink font-body"
                    data-testid="chat-textarea"
                  />
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-zinc-600 font-body">{chatText.length}/1000</span>
                    <button
                      onClick={handleSendMessage}
                      disabled={sending || !chatText.trim()}
                      className="flex items-center gap-1.5 px-4 py-2 bg-neon-pink text-black text-xs font-heading font-bold uppercase rounded-lg hover:bg-neon-pink/80 disabled:opacity-40 transition-colors"
                      data-testid="chat-send-btn"
                    >
                      <Send className="w-3 h-3" />
                      {sending ? "..." : t("chat.send")}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="p-3 space-y-2 max-h-72 overflow-y-auto" data-testid="chat-history">
                  {loadingHistory && <p className="text-xs text-zinc-500 font-body text-center py-4">Cargando...</p>}
                  {!loadingHistory && myMessages.length === 0 && (
                    <p className="text-xs text-zinc-500 font-body text-center py-4">No tienes mensajes enviados.</p>
                  )}
                  {myMessages.map((msg) => (
                    <div key={msg.id} className="space-y-1.5" data-testid={`history-msg-${msg.id}`}>
                      <div className="bg-zinc-800/70 rounded-lg p-2.5">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`text-xs font-heading font-bold uppercase px-1.5 py-0.5 rounded ${msg.type === "duda" ? "bg-blue-500/20 text-blue-400" : "bg-purple-500/20 text-purple-400"}`}>
                            {msg.type === "duda" ? "Duda" : "Mejora"}
                          </span>
                          <span className="text-xs text-zinc-600 font-body">{new Date(msg.created_at).toLocaleString("es-ES", { timeZone: "Europe/Madrid" })}</span>
                        </div>
                        <p className="text-xs text-zinc-300 font-body whitespace-pre-wrap">{msg.content}</p>
                      </div>
                      {(msg.replies || []).map((r) => (
                        <div key={r.id} className="ml-3 bg-neon-pink/10 border border-neon-pink/20 rounded-lg p-2.5" data-testid={`history-reply-${r.id}`}>
                          <p className="text-xs text-neon-pink font-body mb-0.5">{r.admin_name} (Admin) · {new Date(r.created_at).toLocaleString("es-ES", { timeZone: "Europe/Madrid" })}</p>
                          <p className="text-xs text-white font-body whitespace-pre-wrap">{r.content}</p>
                        </div>
                      ))}
                      {(msg.replies || []).length === 0 && (
                        <p className="ml-3 text-xs text-zinc-600 font-body italic">Sin respuesta aún.</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* ── FAB "Chat StarTip" — solo móvil (<sm) ───────────────────────────── */}
      {user && (
        <div
          className="sm:hidden fixed bottom-6 right-0 z-40 flex items-center"
          ref={fabRef}
        >
          {/* Botón principal — anima con maxWidth */}
          <div
            style={{
              maxWidth: fabExpanded ? "180px" : "0px",
              opacity: fabExpanded ? 1 : 0,
              overflow: "hidden",
              transition: "max-width 0.3s ease, opacity 0.2s ease",
              pointerEvents: fabExpanded ? "auto" : "none",
            }}
          >
            <button
              onClick={() => setChatOpen(o => !o)}
              data-testid="chat-admin-fab"
              className="flex items-center gap-2 pl-3 pr-4 py-2.5 font-heading font-bold uppercase text-xs active:scale-95 transition-transform whitespace-nowrap"
              style={{
                background: "rgba(212,175,55,0.13)",
                color: "rgba(212,175,55,0.95)",
                backdropFilter: "blur(8px)",
                WebkitBackdropFilter: "blur(8px)",
              }}
            >
              <MessageCircle className="w-4 h-4 shrink-0" />
              Chat StarTip
              {adminUnread > 0 && (
                <span className="inline-flex items-center justify-center min-w-[16px] h-[16px] px-0.5 rounded-full bg-black/40 text-white text-[9px] font-heading font-bold">
                  {adminUnread > 99 ? "99+" : adminUnread}
                </span>
              )}
            </button>
          </div>

          {/* Tab de plegar/desplegar — siempre visible, estilo dorado */}
          <button
            onClick={() => {
              const next = !fabExpanded;
              setFabExpanded(next);
              try { localStorage.setItem("startip_fab_collapsed", next ? "false" : "true"); } catch {}
            }}
            data-testid="chat-fab-toggle"
            aria-label={fabExpanded ? "Plegar chat" : "Abrir chat StarTip"}
            className="relative w-7 h-11 flex items-center justify-center active:scale-95 transition-transform rounded-l-xl"
            style={{
              background: "rgba(212,175,55,0.13)",
              border: "1.5px solid rgba(212,175,55,0.6)",
              borderRight: "none",
              boxShadow: "0 0 12px rgba(212,175,55,0.18)",
              backdropFilter: "blur(8px)",
              WebkitBackdropFilter: "blur(8px)",
            }}
          >
            <ChevronRight
              className="w-4 h-4 transition-transform duration-300"
              style={{
                color: "rgba(212,175,55,0.95)",
                transform: fabExpanded ? "rotate(0deg)" : "rotate(180deg)",
              }}
            />
            {!fabExpanded && adminUnread > 0 && (
              <span
                className="absolute -top-1.5 -left-1.5 inline-flex items-center justify-center min-w-[16px] h-[16px] px-0.5 rounded-full bg-amber-400 text-black text-[9px] font-heading font-bold animate-bounce"
                data-testid="chat-admin-fab-badge"
              >
                {adminUnread > 99 ? "99+" : adminUnread}
              </span>
            )}
          </button>
        </div>
      )}
    </div>
  );
}
